/**
 * Minimal DXF R12 importer — reads LINE, CIRCLE, ARC, TEXT entities
 * into SVG path data for display on the drawing canvas.
 */
import { SHEET_H } from "./constants";

export interface DxfEntity {
  type: "line" | "circle" | "arc" | "text";
  layer: string;
  data: Record<string, number | string>;
}

export interface DxfImportResult {
  entities: DxfEntity[];
  svgPaths: string[];
  texts: Array<{ x: number; y: number; height: number; text: string }>;
}

function flipY(y: number): number {
  return SHEET_H - y;
}

/** Parse raw DXF text into group code/value pairs */
function parsePairs(content: string): Array<[number, string]> {
  const lines = content.split(/\r?\n/);
  const pairs: Array<[number, string]> = [];
  for (let i = 0; i + 1 < lines.length; i += 2) {
    const code = parseInt(lines[i].trim(), 10);
    const value = lines[i + 1].trim();
    if (!isNaN(code)) {
      pairs.push([code, value]);
    }
  }
  return pairs;
}

/** Extract entities from DXF pairs */
function extractEntities(pairs: Array<[number, string]>): DxfEntity[] {
  const entities: DxfEntity[] = [];
  let inEntities = false;
  let currentType = "";
  let currentLayer = "0";
  let data: Record<string, number | string> = {};

  function flush() {
    if (currentType) {
      entities.push({ type: currentType as DxfEntity["type"], layer: currentLayer, data: { ...data } });
    }
    currentType = "";
    currentLayer = "0";
    data = {};
  }

  for (const [code, value] of pairs) {
    if (code === 0 && value === "SECTION") continue;
    if (code === 2 && value === "ENTITIES") { inEntities = true; continue; }
    if (code === 0 && value === "ENDSEC") { flush(); inEntities = false; continue; }
    if (!inEntities) continue;

    if (code === 0) {
      flush();
      const t = value.toLowerCase();
      if (t === "line" || t === "circle" || t === "arc" || t === "text") {
        currentType = t;
      }
    } else if (code === 8) {
      currentLayer = value;
    } else if (code === 10) data.x1 = parseFloat(value);
    else if (code === 20) data.y1 = parseFloat(value);
    else if (code === 11) data.x2 = parseFloat(value);
    else if (code === 21) data.y2 = parseFloat(value);
    else if (code === 40) data.radius = parseFloat(value);
    else if (code === 50) data.startAngle = parseFloat(value);
    else if (code === 51) data.endAngle = parseFloat(value);
    else if (code === 1) data.text = value;
  }
  flush();
  return entities;
}

/** Convert DXF entities to SVG paths and text annotations */
function entitiesToSvg(entities: DxfEntity[]): DxfImportResult {
  const svgPaths: string[] = [];
  const texts: DxfImportResult["texts"] = [];

  for (const ent of entities) {
    const d = ent.data;
    if (ent.type === "line") {
      const x1 = d.x1 as number;
      const y1 = flipY(d.y1 as number);
      const x2 = d.x2 as number;
      const y2 = flipY(d.y2 as number);
      svgPaths.push(`M ${x1} ${y1} L ${x2} ${y2}`);
    } else if (ent.type === "circle") {
      const cx = d.x1 as number;
      const cy = flipY(d.y1 as number);
      const r = d.radius as number;
      // SVG circle as two arcs
      svgPaths.push(`M ${cx - r} ${cy} A ${r} ${r} 0 1 0 ${cx + r} ${cy} A ${r} ${r} 0 1 0 ${cx - r} ${cy}`);
    } else if (ent.type === "arc") {
      const cx = d.x1 as number;
      const cy = flipY(d.y1 as number);
      const r = d.radius as number;
      const sa = ((d.startAngle as number) || 0) * Math.PI / 180;
      const ea = ((d.endAngle as number) || 360) * Math.PI / 180;
      const sx = cx + r * Math.cos(sa);
      const sy = cy - r * Math.sin(sa); // SVG y-flip
      const ex = cx + r * Math.cos(ea);
      const ey = cy - r * Math.sin(ea);
      let sweep = ea - sa;
      if (sweep < 0) sweep += 2 * Math.PI;
      const largeArc = sweep > Math.PI ? 1 : 0;
      svgPaths.push(`M ${sx} ${sy} A ${r} ${r} 0 ${largeArc} 0 ${ex} ${ey}`);
    } else if (ent.type === "text") {
      texts.push({
        x: d.x1 as number,
        y: flipY(d.y1 as number),
        height: (d.radius as number) || 3, // DXF group code 40 (text height) mapped to 'radius' key
        text: (d.text as string) || "",
      });
    }
  }

  return { entities, svgPaths, texts };
}

/** Import a DXF file string and return SVG-ready data */
export function importDxf(content: string): DxfImportResult {
  const pairs = parsePairs(content);
  const entities = extractEntities(pairs);
  return entitiesToSvg(entities);
}
